import { Checkbox } from "@alch/ui";
import clsx from "clsx";
import { ToggleGroup } from "radix-ui";
import { forwardRef } from "react";

const rootClassName = clsx("flex flex-wrap gap-2");

const itemClassName = clsx(
  "group flex items-center gap-2 px-3 py-2 text-left",
  "rounded-lg bg-card shadow-border shadow-primary transition",
  "enabled:hover:shadow-border-2 enabled:hover:shadow-brand",
  "data-[state=on]:bg-brand-subtle data-[state=on]:shadow-border-2 data-[state=on]:shadow-brand",
  "outline-hidden focus-visible:shadow-focus data-[state=on]:focus-visible:shadow-focus",
);

const labelClassName = clsx(
  "flex items-center gap-1 text-paragraph-200-regular text-secondary transition",
  "group-hover:group-enabled::text-primary group-data-[state=on]:text-primary",
  "group-disabled:opacity-50",
);

const checkboxClassName = clsx(
  "group-hover:enabled:border-brand group-hover:enabled:bg-brand-light",
);

interface SimpleToggleItem {
  label: string;
  value: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

type SimpleToggleGroupProps = (
  | ToggleGroup.ToggleGroupSingleProps
  | ToggleGroup.ToggleGroupMultipleProps
) & {
  items: SimpleToggleItem[];
  onHighlight?: (value?: string) => void;
};

interface SimpleToggleItemProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  groupType: SimpleToggleGroupProps["type"];
  item: SimpleToggleItem;
  "data-state"?: string;
}

const SimpleToggleItem = forwardRef<HTMLButtonElement, SimpleToggleItemProps>(
  function SimpleToggleItem({ groupType, item, className, ...props }, ref) {
    return (
      <button className={clsx(itemClassName, className)} ref={ref} {...props}>
        {groupType === "multiple" && (
          <div
            onClick={(event) => event.preventDefault()}
            className="pointer-events-none"
          >
            <Checkbox
              as="div"
              className={checkboxClassName}
              checked={props["data-state"] === "on"}
              size="sm"
              disabled={item.disabled}
            />
          </div>
        )}

        <div className={labelClassName}>
          {item.icon}
          {item.label}
        </div>
      </button>
    );
  },
);

const SimpleToggleGroup = ({
  items,
  className,
  onHighlight,
  ...props
}: SimpleToggleGroupProps) => {
  return (
    <ToggleGroup.Root
      className={clsx(rootClassName, className)}
      onMouseLeave={() => onHighlight?.()}
      onBlur={() => onHighlight?.()}
      {...props}
    >
      {items.map((item) => (
        <ToggleGroup.Item
          asChild
          key={item.value}
          value={item.value}
          disabled={item.disabled}
          onFocus={() => onHighlight?.(item.value)}
          onMouseEnter={() => onHighlight?.(item.value)}
        >
          <SimpleToggleItem groupType={props.type} item={item} />
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
};

export default SimpleToggleGroup;
